import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { NavLink, Outlet } from 'react-router-dom'
import { fetchData, fetchJson } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import {
  Button,
  Card,
  Empty,
  ErrorBanner,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Tag,
  Textarea,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'
import { useProjects } from '../projects/useProjects'

const MANPOWER_CATEGORIES = [
  'casting',
  'depart_general',
  'steelwork',
  'shuttering',
  'plumbing',
  'masonry',
  'flooring',
  'electrical',
  'other',
] as const
type ManpowerCategory = (typeof MANPOWER_CATEGORIES)[number]

type ExportResponse = { format: string; content: string; pdf_url?: string | null }

function openExportedPdf(res: ExportResponse) {
  if (res.pdf_url) {
    window.open(res.pdf_url, '_blank', 'noopener,noreferrer')
  }
}

const TABS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '', label: 'Manpower', end: true },
  { to: 'stock', label: 'Stock' },
]

export function ReportsLayout() {
  return (
    <div>
      <PageHeader title="Reports" />
      <nav className="mb-4 flex gap-1 border-b border-surface-700">
        {TABS.map((t) => (
          <NavLink
            key={t.label}
            to={t.to}
            end={t.end}
            className={({ isActive }) =>
              `px-3 py-2 text-sm border-b-2 -mb-px ${
                isActive
                  ? 'border-accent-500 text-accent-400'
                  : 'border-transparent text-surface-400 hover:text-surface-100'
              }`
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}

type ManpowerListItem = {
  id: string
  report_date: number
  total_manpower: number
  status: 'draft' | 'submitted'
  project_name: string
  project_code: string
  created_by_name: string
  total_entries: number
}

type ManpowerListResponse = {
  reports: ManpowerListItem[]
  total: number
}

// report_date is stored as YYYYMMDD integer, not a unix timestamp.
function formatReportDate(yyyymmdd: number | undefined): string {
  if (!yyyymmdd) return '—'
  const s = String(yyyymmdd)
  if (s.length !== 8) return s
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
}

export function ManpowerReportPage() {
  const qc = useQueryClient()
  const q = useQuery<ManpowerListResponse>({
    queryKey: ['manpower-report'],
    queryFn: () => fetchData<ManpowerListResponse>(orgPath('/reports/manpower')),
  })

  const submit = useMutation({
    mutationFn: (reportId: string) =>
      fetchJson(orgPath(`/reports/manpower/${reportId}/submit`), { method: 'POST' }),
    onError: flagIfAuthError,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manpower-report'] }),
  })

  const exportPdf = useMutation({
    mutationFn: (reportId: string) =>
      fetchData<ExportResponse>(
        orgPath(`/reports/manpower/${reportId}/export?format=pdf`),
      ),
    onError: flagIfAuthError,
    onSuccess: openExportedPdf,
  })

  const [creating, setCreating] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)

  if (q.isLoading) return <Loading />
  if (q.isError) return <ErrorBanner error={q.error} />
  const reports = q.data?.reports ?? []
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          variant={creating ? 'ghost' : 'primary'}
          onClick={() => setCreating((v) => !v)}
        >
          {creating ? 'Cancel' : '+ New manpower report'}
        </Button>
      </div>
      {creating && (
        <NewManpowerReportForm
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false)
            qc.invalidateQueries({ queryKey: ['manpower-report'] })
          }}
        />
      )}
      {reports.length === 0 ? (
        <Empty message="No manpower reports yet." />
      ) : (
        <Card>
          {(submit.isError || exportPdf.isError) && (
            <ErrorBanner error={submit.error ?? exportPdf.error} />
          )}
          <ul className="divide-y divide-surface-700 text-sm">
            {reports.map((r) => {
              const isOpen = expanded === r.id
              return (
                <li key={r.id} className="py-2">
                  <div className="flex justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                        className="text-surface-400 hover:text-accent-400 mt-0.5 w-5 text-left"
                        onClick={() => setExpanded((cur) => (cur === r.id ? null : r.id))}
                      >
                        {isOpen ? '▾' : '▸'}
                      </button>
                      <div>
                        <div className="font-medium">{r.project_name}</div>
                        <div className="text-xs text-surface-400">
                          {formatReportDate(r.report_date)} · by {r.created_by_name}
                        </div>
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div>{r.total_manpower} workers</div>
                      <div>
                        <Tag tone={r.status === 'submitted' ? 'success' : 'warn'}>
                          {r.status}
                        </Tag>
                      </div>
                      <div className="flex gap-2 justify-end">
                        {r.status !== 'submitted' && (
                          <Button
                            variant="ghost"
                            onClick={() => submit.mutate(r.id)}
                            disabled={submit.isPending && submit.variables === r.id}
                          >
                            {submit.isPending && submit.variables === r.id
                              ? 'Submitting…'
                              : 'Submit'}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          onClick={() => exportPdf.mutate(r.id)}
                          disabled={exportPdf.isPending && exportPdf.variables === r.id}
                        >
                          {exportPdf.isPending && exportPdf.variables === r.id
                            ? 'Exporting…'
                            : 'Export PDF'}
                        </Button>
                      </div>
                    </div>
                  </div>
                  {isOpen && <ManpowerEntries reportId={r.id} />}
                </li>
              )
            })}
          </ul>
        </Card>
      )}
    </div>
  )
}

type ManpowerEntry = {
  id: string
  category: string
  skilled_count: number
  unskilled_count: number
  remarks: string | null
}

function ManpowerEntries({ reportId }: { reportId: string }) {
  const q = useQuery<{ entries: ManpowerEntry[] }>({
    queryKey: ['manpower-report', reportId],
    queryFn: () =>
      fetchData<{ entries: ManpowerEntry[] }>(orgPath(`/reports/manpower/${reportId}`)),
  })
  if (q.isLoading) return <Loading />
  if (q.isError) return <ErrorBanner error={q.error} />
  const entries = (q.data?.entries ?? []).filter(
    (e) => e.skilled_count > 0 || e.unskilled_count > 0,
  )
  if (entries.length === 0) {
    return (
      <p className="mt-3 ml-7 text-xs text-surface-400">No category entries.</p>
    )
  }
  return (
    <div className="mt-3 ml-7 border-t border-surface-700/60 pt-3">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-surface-400">
            <th className="py-1 pr-3">Category</th>
            <th className="py-1 pr-3">Skilled</th>
            <th className="py-1 pr-3">Unskilled</th>
            <th className="py-1">Remarks</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} className="border-t border-surface-800">
              <td className="py-1 pr-3 capitalize">{e.category.replace('_', ' ')}</td>
              <td className="py-1 pr-3">{e.skilled_count}</td>
              <td className="py-1 pr-3">{e.unskilled_count}</td>
              <td className="py-1 text-surface-300">{e.remarks ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function todayYyyymmdd(): number {
  const d = new Date()
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate()
}

type EntryState = { skilled: string; unskilled: string; remarks: string }

function NewManpowerReportForm({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const projects = useProjects()
  const [projectId, setProjectId] = useState('')
  const [entries, setEntries] = useState<Record<ManpowerCategory, EntryState>>(
    () =>
      Object.fromEntries(
        MANPOWER_CATEGORIES.map((c) => [c, { skilled: '', unskilled: '', remarks: '' }]),
      ) as Record<ManpowerCategory, EntryState>,
  )

  const create = useMutation({
    mutationFn: () => {
      const payload = {
        project_id: projectId,
        report_date: todayYyyymmdd(),
        entries: MANPOWER_CATEGORIES.map((c) => ({
          category: c,
          skilled_count: Number(entries[c].skilled || 0),
          unskilled_count: Number(entries[c].unskilled || 0),
          remarks: entries[c].remarks || undefined,
        })).filter((e) => e.skilled_count > 0 || e.unskilled_count > 0),
      }
      return fetchJson(orgPath('/reports/manpower'), {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    },
    onError: flagIfAuthError,
    onSuccess: onCreated,
  })

  const total = MANPOWER_CATEGORIES.reduce(
    (sum, c) => sum + Number(entries[c].skilled || 0) + Number(entries[c].unskilled || 0),
    0,
  )

  return (
    <Card>
      <h3 className="text-sm font-semibold mb-3">New manpower report (today)</h3>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (!projectId || total === 0) return
          create.mutate()
        }}
      >
        <Field label="Project">
          <Select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            required
          >
            <option value="">Select…</option>
            {(projects.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-surface-400 border-b border-surface-700">
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3">Skilled</th>
                <th className="py-2 pr-3">Unskilled</th>
                <th className="py-2">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {MANPOWER_CATEGORIES.map((c) => (
                <tr key={c} className="border-b border-surface-700/50">
                  <td className="py-2 pr-3 capitalize">{c.replace('_', ' ')}</td>
                  <td className="py-2 pr-3">
                    <Input
                      type="number"
                      min={0}
                      value={entries[c].skilled}
                      onChange={(e) =>
                        setEntries((prev) => ({
                          ...prev,
                          [c]: { ...prev[c], skilled: e.target.value },
                        }))
                      }
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <Input
                      type="number"
                      min={0}
                      value={entries[c].unskilled}
                      onChange={(e) =>
                        setEntries((prev) => ({
                          ...prev,
                          [c]: { ...prev[c], unskilled: e.target.value },
                        }))
                      }
                    />
                  </td>
                  <td className="py-2">
                    <Textarea
                      rows={1}
                      value={entries[c].remarks}
                      onChange={(e) =>
                        setEntries((prev) => ({
                          ...prev,
                          [c]: { ...prev[c], remarks: e.target.value },
                        }))
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-surface-400">Total workers: {total}</div>
        {create.isError && <ErrorBanner error={create.error} />}
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={create.isPending || !projectId || total === 0}
          >
            {create.isPending ? 'Saving…' : 'Save report'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

type StockSummary = {
  total_materials: number
  low_stock_count: number
  pending_requests: number
}

type StockByCategory = {
  category: string
  total_quantity: number
  unit: string
  low_stock: boolean
  pending_requests: number
}

type LowStockItem = {
  material_id: string
  material_name: string
  material_code: string
  current_quantity: number
  minimum_level: number
  unit: string
  project_name: string
}

type StockReport = {
  generated_at: number
  summary: StockSummary
  by_category: StockByCategory[]
  by_project: Array<{ project_id: string; project_name: string; materials_count: number; low_stock_items: number }>
  low_stock_items: LowStockItem[]
}

export function StockReportPage() {
  const q = useQuery<StockReport>({
    queryKey: ['stock-report'],
    queryFn: () => fetchData<StockReport>(orgPath('/reports/stock-report')),
  })

  const exportPdf = useMutation({
    mutationFn: () =>
      fetchData<ExportResponse>(orgPath('/reports/stock-report/export?format=pdf')),
    onError: flagIfAuthError,
    onSuccess: openExportedPdf,
  })

  if (q.isLoading) return <Loading />
  if (q.isError) return <ErrorBanner error={q.error} />
  if (!q.data) return <Empty message="No stock data yet." />

  const { summary, by_category, low_stock_items } = q.data

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => exportPdf.mutate()} disabled={exportPdf.isPending}>
          {exportPdf.isPending ? 'Exporting…' : 'Export PDF'}
        </Button>
      </div>
      {exportPdf.isError && <ErrorBanner error={exportPdf.error} />}
      <Card>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs text-surface-400">Total materials</div>
            <div className="text-lg font-semibold">{summary.total_materials}</div>
          </div>
          <div>
            <div className="text-xs text-surface-400">Low stock</div>
            <div className="text-lg font-semibold">{summary.low_stock_count}</div>
          </div>
          <div>
            <div className="text-xs text-surface-400">Pending requests</div>
            <div className="text-lg font-semibold">{summary.pending_requests}</div>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-sm font-semibold mb-2">By category</h3>
        {by_category.length === 0 ? (
          <Empty message="No category data." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-400 border-b border-surface-700">
                  <th className="py-2 pr-4">Category</th>
                  <th className="py-2 pr-4">Quantity</th>
                  <th className="py-2 pr-4">Unit</th>
                  <th className="py-2 pr-4">Pending</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {by_category.map((r) => (
                  <tr key={r.category} className="border-b border-surface-700/50">
                    <td className="py-2 pr-4">{r.category}</td>
                    <td className="py-2 pr-4">{r.total_quantity}</td>
                    <td className="py-2 pr-4">{r.unit}</td>
                    <td className="py-2 pr-4">{r.pending_requests}</td>
                    <td className="py-2">
                      {r.low_stock ? (
                        <Tag tone="danger">Low stock</Tag>
                      ) : (
                        <Tag tone="success">OK</Tag>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {low_stock_items.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold mb-2">Low stock items</h3>
          <ul className="divide-y divide-surface-700 text-sm">
            {low_stock_items.map((m) => (
              <li key={`${m.material_id}-${m.project_name}`} className="py-2 flex justify-between gap-3">
                <div>
                  <div className="font-medium">{m.material_name}</div>
                  <div className="text-xs text-surface-400">
                    {m.material_code} · {m.project_name}
                  </div>
                </div>
                <div className="text-right text-xs text-surface-400">
                  <div>
                    <span className="text-rose-300">{m.current_quantity}</span> / {m.minimum_level} {m.unit}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  )
}
